package ui.app.assign

import androidx.compose.runtime.*
import common.frontend.api.Io
import org.jetbrains.compose.resources.StringResource
import provider.LocalAccountProvider
import provider.LocalLocalization
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tools.EFieldList
import tools.EFieldTextString


interface AppAssignListingScope {

    val vm: AppAssignListingVM
    val action: (AppAssignListingVM.Action) -> Unit

    val nav: Navigation
    val login: Io.User
    val args: AppAssignListingArgs
    val lang: String

    companion object {

        @Composable
        fun rememberScope(): AppAssignListingScope {

            val account = LocalAccountProvider.current
            val nav = LocalNavigation.current
            val args = LocalNavigationArgs.current
            val arg = args.rememberArgs(AppAssignListingArgs::class)
            val vm = nav.viewModel(AppAssignListingVM::class, arg.companyId.toString())
            val action = AppAssignListingVM.Action.rememberAction(vm)
            val lang = LocalLocalization.current.language.short.first()

            return remember {
                object : AppAssignListingScope {
                    override val vm: AppAssignListingVM
                        get() = vm
                    override val action: (AppAssignListingVM.Action) -> Unit
                        get() = action
                    override val nav: Navigation
                        get() = nav
                    override val login: Io.User
                        get() = account.current!!
                    override val args: AppAssignListingArgs
                        get() = arg
                    override val lang: String
                        get() = lang
                }.also {
                    vm.init(it)
                }
            }
        }
    }

    sealed class Tab(
        val label: StringResource,
        val emptyLabel: StringResource,
        val filter: (items: List<Wrapper.Item>) -> List<Wrapper.Item>,
        val counter: (items: List<Wrapper.Item>) -> Int = { filter(it).size }
    ) {

        data object Active : Tab(
            label = Res.string.layer_bottom_listing__tab_active_label,
            emptyLabel = Res.string.layer_bottom_listing__tab_active_empty,
            filter = { items -> items.filter { it.data.connection.isNotEmpty() || it.data.campaigns.isNotEmpty() || it.data.devices.isNotEmpty() || !it.data.archived } })

        data object Unused : Tab(
            label = Res.string.layer_bottom_listing__tab_unused_label,
            emptyLabel = Res.string.layer_bottom_listing__tab_unused_empty,
            filter = { items -> items.filter { it.data.connection.isEmpty() && it.data.campaigns.isEmpty() && it.data.devices.isEmpty() && !it.data.archived } })

        data object Archived : Tab(
            label = Res.string.layer_bottom_listing__tab_archived_label,
            emptyLabel = Res.string.layer_bottom_listing__tab_archived_empty,
            filter = { items -> items.filter { it.data.archived } })
    }

    class Wrapper(
        val canAdd: Boolean,
        origin: Io.ListingApp
    ) : EFieldList<Io.ListingApp.Item, Wrapper.Item>(
        items = origin.items,
        onBindEntry = { Item(it) }
    ) {
        class Item(
            override val data: Io.ListingApp.Item,
            override val key: String = data.id.toString(),

            ) : Entry<Io.ListingApp.Item> {
            private var origin = data.copy()

            val name: EFieldTextString = EFieldTextString(
                property = data::name,
                condition = { it.trim().takeIf { it.length > 4 } },
                onChange = { makeChange() },
                onErr = { makeChange() })

            var change by mutableStateOf(false)
                private set

            private fun makeChange() {
                if (name.err) {
                    change = false
                } else if (origin.name != data.name) {
                    change = true
                }
            }
        }
    }
}
